(() => {
  const MAX_REVIEW_IMAGE_BYTES = 10 * 1024 * 1024;
  const REVIEW_IMAGE_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
  ]);

  const cleanText = (value, maximum = 280) => String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maximum);

  const trackedUrl = (rawUrl, campaign) => {
    try {
      const url = new URL(rawUrl, window.location.origin);
      url.searchParams.set("utm_source", "customer");
      url.searchParams.set("utm_medium", "social");
      url.searchParams.set("utm_campaign", campaign || "exo_customer_review");
      return url.toString();
    } catch (_error) {
      return rawUrl || window.location.href;
    }
  };

  const copyText = async (value) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const field = document.createElement("textarea");
    field.value = value;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.append(field);
    field.select();
    document.execCommand("copy");
    field.remove();
  };

  class ExoReviewHub extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.scopeControl = this.querySelector("[data-exo-review-scope]");
      this.sortControl = this.querySelector("[data-exo-review-sort]");
      this.list = this.querySelector("[data-exo-review-list]");
      this.status = this.querySelector("[data-exo-review-status]");
      this.fallback = this.querySelector("[data-exo-review-fallback]");
      this.cards = Array.from(this.querySelectorAll("[data-exo-review-card]"));
      this.currentProductId = String(this.dataset.currentProductId || "");
      this.hasProductRating = this.dataset.hasProductRating === "true";
      this.hasStoreRating = this.dataset.hasStoreRating === "true";
      this.productTitle = cleanText(this.dataset.reviewProductTitle, 120);
      this.productHandle = cleanText(this.dataset.reviewProductHandle, 120) || "product";
      this.productUrl = this.dataset.reviewProductUrl || window.location.href;
      this.productImage = this.dataset.reviewProductImage || "";
      this.shareCampaign = cleanText(this.dataset.reviewShareCampaign, 120) || "exo_customer_review";
      this.uploadEndpoint = String(this.dataset.reviewMediaUploadEndpoint || "").replace(/\/$/, "");
      this.storageKey = `exo-review-draft:${this.productHandle}`;

      this.setupReviewList();
      this.setupPublishedReviewSharing();
      this.setupSubmission();
      this.setupSuccess();
    }

    setupReviewList() {
      if (!this.list || !this.scopeControl || !this.sortControl) return;

      if (!this.currentProductId) {
        this.scopeControl.value = "business";
        const productOption = this.scopeControl.querySelector('option[value="product"]');
        if (productOption) productOption.disabled = true;
      }

      this.scopeControl.addEventListener("change", () => this.render());
      this.sortControl.addEventListener("change", () => this.render());
      this.render();
    }

    render() {
      const scope = this.scopeControl.value;
      const sort = this.sortControl.value;
      const matching = this.cards.filter((card) => {
        if (scope === "business") return true;
        return String(card.dataset.productId || "") === this.currentProductId;
      });

      matching.sort((left, right) => {
        const leftRating = Number.parseFloat(left.dataset.rating || "0");
        const rightRating = Number.parseFloat(right.dataset.rating || "0");

        if (sort === "highest") return rightRating - leftRating;
        if (sort === "lowest") return leftRating - rightRating;

        const leftDate = Date.parse(left.dataset.reviewedAt || "") || 0;
        const rightDate = Date.parse(right.dataset.reviewedAt || "") || 0;
        return rightDate - leftDate;
      });

      this.cards.forEach((card) => {
        card.hidden = true;
      });

      const visibleNodes = matching.map((card) => {
        card.hidden = false;
        return card;
      });

      if (matching.length === 0 && this.fallback) {
        this.fallback.hidden = false;
        visibleNodes.push(this.fallback);
      } else if (this.fallback) {
        this.fallback.hidden = true;
      }

      this.list.replaceChildren(...visibleNodes);
      this.updateStatus(scope, matching.length);
    }

    updateStatus(scope, count) {
      if (!this.status) return;

      if (count > 0) {
        const label = count === 1 ? "review" : "reviews";
        this.status.textContent = `Showing ${count} ${scope === "product" ? "product" : "business-wide"} ${label}.`;
        return;
      }

      if (this.hasStoreRating) {
        this.status.textContent = scope === "product"
          ? "Showing EXO's verified store rating while this product gathers its own reviews."
          : "Showing EXO's verified business-wide store rating.";
        return;
      }

      this.status.textContent = scope === "product"
        ? "Be the first to review this product."
        : "No governed business-wide rating is available yet.";
    }

    setupPublishedReviewSharing() {
      this.querySelectorAll("[data-exo-review-share]").forEach((button) => {
        button.addEventListener("click", async () => {
          const rating = cleanText(button.dataset.reviewRating, 3);
          const title = cleanText(button.dataset.reviewTitle, 100);
          const body = cleanText(button.dataset.reviewBody, 180);
          const product = cleanText(button.dataset.reviewProductTitle, 120) || this.productTitle;
          const url = trackedUrl(button.dataset.reviewProductUrl || this.productUrl, this.shareCampaign);
          const shareText = [
            rating ? `${rating}/5 for ${product}.` : `A customer review of ${product}.`,
            title,
            body,
          ].filter(Boolean).join(" ");

          try {
            if (navigator.share) {
              await navigator.share({ title: `Customer review: ${product}`, text: shareText, url });
            } else {
              await copyText(`${shareText}\n${url}`);
              button.textContent = "Review link copied";
            }
          } catch (error) {
            if (error?.name !== "AbortError") {
              await copyText(`${shareText}\n${url}`);
              button.textContent = "Review link copied";
            }
          }
        });
      });
    }

    setupSubmission() {
      const form = this.querySelector(".exo-review-hub__form");
      const input = this.querySelector("[data-exo-review-media-input]");
      if (!form || !input || !this.uploadEndpoint) {
        if (form) form.addEventListener("submit", () => this.saveDraft(form));
        return;
      }

      const preview = this.querySelector("[data-exo-review-media-preview]");
      const image = this.querySelector("[data-exo-review-media-image]");
      const name = this.querySelector("[data-exo-review-media-name]");
      const remove = this.querySelector("[data-exo-review-media-remove]");
      const status = this.querySelector("[data-exo-review-media-status]");
      const consent = this.querySelector("[data-exo-review-media-consent-input]");
      const reference = this.querySelector("[data-exo-review-media-reference]");
      const submit = form.querySelector('button[type="submit"]');
      let previewUrl = "";

      const setStatus = (message, state = "") => {
        if (!status) return;
        status.textContent = message;
        status.dataset.state = state;
      };

      const clearFile = () => {
        input.value = "";
        if (consent) {
          consent.required = false;
          consent.checked = false;
        }
        if (preview) preview.hidden = true;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        previewUrl = "";
        setStatus("");
      };

      input.addEventListener("change", () => {
        const file = input.files?.[0];
        if (!file) {
          clearFile();
          return;
        }

        if (!this.isAcceptedImage(file)) {
          clearFile();
          setStatus("Choose a JPEG, PNG, WebP, HEIC, or HEIF image.", "error");
          return;
        }

        if (file.size > MAX_REVIEW_IMAGE_BYTES) {
          clearFile();
          setStatus("That image is over 10 MB. Choose a smaller photo.", "error");
          return;
        }

        if (previewUrl) URL.revokeObjectURL(previewUrl);
        previewUrl = URL.createObjectURL(file);
        if (image) image.src = previewUrl;
        if (name) name.textContent = `${file.name} · ${(file.size / 1024 / 1024).toFixed(1)} MB`;
        if (preview) preview.hidden = false;
        if (consent) consent.required = true;
        setStatus("Photo ready. Complete the permission checkbox before sending.", "ready");
      });

      remove?.addEventListener("click", clearFile);

      form.addEventListener("submit", async (event) => {
        this.saveDraft(form);
        const file = input.files?.[0];
        if (!file) return;

        event.preventDefault();
        if (!form.reportValidity()) return;

        const turnstileToken = form.querySelector('input[name="cf-turnstile-response"]')?.value;
        if (!turnstileToken) {
          setStatus("Please complete the secure photo check, then send again.", "error");
          return;
        }

        submit.disabled = true;
        submit.setAttribute("aria-busy", "true");
        setStatus("Securing your photo…", "working");

        try {
          const contentType = this.normalizedImageType(file);
          const sessionResponse = await fetch(`${this.uploadEndpoint}/v1/review-media/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              productHandle: this.productHandle,
              fileName: file.name,
              contentType,
              size: file.size,
              turnstileToken,
            }),
          });
          const session = await sessionResponse.json().catch(() => ({}));
          if (!sessionResponse.ok || !session.uploadUrl || !session.uploadToken) {
            throw new Error(session.error || "The secure upload could not start.");
          }

          setStatus("Uploading your private review photo…", "working");
          const uploadResponse = await fetch(session.uploadUrl, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${session.uploadToken}`,
              "Content-Type": contentType,
              "X-Upload-Bytes": String(file.size),
            },
            body: file,
          });
          const upload = await uploadResponse.json().catch(() => ({}));
          if (!uploadResponse.ok || !upload.mediaReference) {
            throw new Error(upload.error || "The photo could not be uploaded.");
          }

          reference.value = upload.mediaReference;
          this.saveDraft(form, upload.mediaReference);
          setStatus("Photo secured. Sending your review…", "working");
          form.submit();
        } catch (error) {
          setStatus(error?.message || "The photo could not be uploaded. Please try again.", "error");
          submit.disabled = false;
          submit.removeAttribute("aria-busy");
          window.turnstile?.reset?.();
        }
      });
    }

    isAcceptedImage(file) {
      if (REVIEW_IMAGE_TYPES.has(String(file.type || "").toLowerCase())) return true;
      return /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "");
    }

    normalizedImageType(file) {
      const provided = String(file.type || "").toLowerCase();
      if (REVIEW_IMAGE_TYPES.has(provided)) return provided;
      const extension = String(file.name || "").split(".").pop()?.toLowerCase();
      return ({ jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", heic: "image/heic", heif: "image/heif" })[extension] || "application/octet-stream";
    }

    saveDraft(form, mediaReference = "") {
      try {
        const fields = new FormData(form);
        const existing = JSON.parse(sessionStorage.getItem(this.storageKey) || "{}");
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          submissionId: cleanText(existing.submissionId, 80) || crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          email: cleanText(fields.get("contact[email]"), 254),
          marketingOptIn: fields.get("contact[Email marketing opt in]") === "Granted",
          rating: cleanText(fields.get("contact[Review rating]"), 3),
          title: cleanText(fields.get("contact[Review title]"), 100),
          body: cleanText(fields.get("contact[body]"), 280),
          product: this.productTitle,
          productUrl: this.productUrl,
          mediaReference: cleanText(mediaReference, 500),
          savedAt: Date.now(),
        }));
      } catch (_error) {
        // Submission must remain usable when storage is unavailable.
      }
    }

    setupSuccess() {
      const success = this.querySelector("[data-exo-review-success]");
      if (!success) return;

      let draft = {};
      try {
        draft = JSON.parse(sessionStorage.getItem(this.storageKey) || "{}");
        sessionStorage.removeItem(this.storageKey);
      } catch (_error) {
        draft = {};
      }

      const rating = cleanText(draft.rating, 3);
      const title = cleanText(draft.title, 100);
      const quote = cleanText(draft.body, 180) || "I just shared my experience.";
      const product = cleanText(draft.product, 120) || this.productTitle;
      const url = trackedUrl(draft.productUrl || this.productUrl, this.shareCampaign);
      const caption = [
        rating ? `I just gave ${product} ${rating}/5 stars.` : `I just reviewed ${product}.`,
        title,
        quote,
        url,
      ].filter(Boolean).join("\n\n");

      const ratingNode = success.querySelector("[data-exo-review-share-rating]");
      const productNode = success.querySelector("[data-exo-review-share-product]");
      const quoteNode = success.querySelector("[data-exo-review-share-quote]");
      const status = success.querySelector("[data-exo-review-success-status]");
      if (ratingNode) ratingNode.textContent = rating ? `${rating}/5 CUSTOMER REVIEW` : "CUSTOMER REVIEW";
      if (productNode) productNode.textContent = product;
      if (quoteNode) quoteNode.textContent = `“${quote}”`;

      const setStatus = (message) => {
        if (status) status.textContent = message;
      };

      this.submitMarketingOptIn(draft, setStatus);

      success.querySelector("[data-exo-review-success-copy]")?.addEventListener("click", async () => {
        await copyText(caption);
        setStatus("Caption and product link copied.");
      });

      success.querySelectorAll("[data-exo-review-success-platform]").forEach((button) => {
        button.addEventListener("click", async () => {
          const platform = button.dataset.exoReviewSuccessPlatform;
          window.open(platform === "tiktok" ? "https://www.tiktok.com/upload" : "https://www.instagram.com/", "_blank", "noopener,noreferrer");
          await copyText(caption);
          setStatus(`Caption copied. Opening ${platform === "tiktok" ? "TikTok" : "Instagram"}…`);
        });
      });

      success.querySelector("[data-exo-review-success-share]")?.addEventListener("click", async () => {
        const file = await this.createShareCard({ rating, quote, product });
        try {
          if (file && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: `My review of ${product}`, text: caption });
            setStatus("Thanks for sharing your experience.");
          } else if (navigator.share) {
            await navigator.share({ title: `My review of ${product}`, text: caption, url });
            setStatus("Thanks for sharing your experience.");
          } else {
            if (file) this.downloadShareCard(file);
            await copyText(caption);
            setStatus("Share card downloaded and caption copied.");
          }
        } catch (error) {
          if (error?.name !== "AbortError") {
            await copyText(caption);
            setStatus("Caption copied. Paste it into your story or feed.");
          }
        }
      });

      success.focus({ preventScroll: true });
    }

    async submitMarketingOptIn(draft, setStatus) {
      if (draft.marketingOptIn !== true || !draft.email) return;

      const form = this.querySelector(".exo-review-marketing-form");
      const email = form?.querySelector("[data-exo-review-marketing-email]");
      if (!form || !email) {
        setStatus("Your review is in. We could not record the optional email preference; please use the site signup form if you still want updates.");
        return;
      }

      const submissionId = cleanText(draft.submissionId, 80);
      const marker = submissionId ? `exo-review-marketing:${submissionId}` : "";
      try {
        if (marker && sessionStorage.getItem(marker) === "complete") return;
        email.value = draft.email;
        const response = await fetch(form.action, {
          method: "POST",
          body: new FormData(form),
          credentials: "same-origin",
          headers: { Accept: "text/html" },
        });
        if (!response.ok) throw new Error("Marketing preference request failed");
        if (marker) sessionStorage.setItem(marker, "complete");
        setStatus("Your optional Gameday Flags email preference was recorded.");
      } catch (_error) {
        setStatus("Your review is in. We could not record the optional email preference; please use the site signup form if you still want updates.");
      }
    }

    async createShareCard({ rating, quote, product }) {
      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1350;
      const context = canvas.getContext("2d");
      if (!context) return null;

      const gradient = context.createLinearGradient(0, 0, 1080, 1350);
      gradient.addColorStop(0, "#10120f");
      gradient.addColorStop(0.7, "#20251e");
      gradient.addColorStop(1, "#a9dd00");
      context.fillStyle = gradient;
      context.fillRect(0, 0, 1080, 1350);
      context.strokeStyle = "#a9dd00";
      context.lineWidth = 10;
      context.strokeRect(48, 48, 984, 1254);

      context.fillStyle = "#a9dd00";
      context.font = "800 34px Arial, sans-serif";
      context.fillText("GAMEDAY FLAGS / CUSTOMER REVIEW", 92, 130);

      context.fillStyle = "#ffffff";
      context.font = "800 78px Arial, sans-serif";
      this.drawWrappedText(context, product, 92, 260, 850, 88, 2);

      context.fillStyle = "#a9dd00";
      context.font = "800 48px Arial, sans-serif";
      context.fillText(rating ? `${rating}/5 STARS` : "REVIEWED", 92, 492);

      context.fillStyle = "#ffffff";
      context.font = "600 52px Arial, sans-serif";
      this.drawWrappedText(context, `“${quote}”`, 92, 620, 850, 70, 6);

      context.fillStyle = "#10120f";
      context.font = "800 34px Arial, sans-serif";
      context.fillText("MAKE YOUR PLACE THE PLACE TO BE.", 92, 1240);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.94));
      return blob ? new File([blob], "gameday-flags-review.png", { type: "image/png" }) : null;
    }

    drawWrappedText(context, value, x, y, maximumWidth, lineHeight, maximumLines) {
      const words = cleanText(value, 500).split(" ");
      let line = "";
      let row = 0;
      for (let index = 0; index < words.length; index += 1) {
        const testLine = `${line}${words[index]} `;
        if (context.measureText(testLine).width > maximumWidth && line) {
          context.fillText(line.trim(), x, y + (row * lineHeight));
          line = `${words[index]} `;
          row += 1;
          if (row >= maximumLines - 1) break;
        } else {
          line = testLine;
        }
      }
      if (line && row < maximumLines) context.fillText(line.trim(), x, y + (row * lineHeight));
    }

    downloadShareCard(file) {
      const link = document.createElement("a");
      const url = URL.createObjectURL(file);
      link.href = url;
      link.download = file.name;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }

  if (!window.customElements.get("exo-review-hub")) {
    window.customElements.define("exo-review-hub", ExoReviewHub);
  }
})();
